import { Injectable } from '@angular/core';
import { SafeResourceUrl } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { ApiService } from './api.service';
import * as FileSaver from 'file-saver';
import { environment } from 'src/environments/environment.prod';
import { HttpClient } from '@angular/common/http';

@Injectable({
  providedIn: 'root'
})
export class FileUtilsService {
  mimeType: string;
  showFrame = false;
  url: SafeResourceUrl;

  constructor(
    private toastr: ToastrService,
    private translate: TranslateService,
    private apiService: ApiService,
    private http: HttpClient
  ) { }


  private visualizables = [
    'application/pdf',
    'image/jpeg',
    'image/png',
    'image/gif',
    'text/plain',
    'video/mp4',
    'audio/mpeg',
    'text/html',
    'application/msword',
    'application/vnd.ms-excel',
    'application/vnd.ms-powerpoint'
  ];

  /**
   * Returns the MIME type for a given file extension.
   */
  getMimeType(tipo_archivo: string): string | null {
    switch (tipo_archivo.toLowerCase()) {
      case 'pdf': return 'application/pdf';
      case 'jpe':
      case 'jpeg':
      case 'jpg': return 'image/jpeg';
      case 'png': return 'image/png';
      case 'gif': return 'image/gif';
      case 'txt':
      case 'bas':
      case 'c':
      case 'h': return 'text/plain';
      case 'mp4': return 'video/mp4';
      case 'mp3':
      case 'aac': return 'audio/mpeg';
      case 'html':
      case 'htm':
      case 'stm': return 'text/html';
      case 'doc':
      case 'docx': return 'application/msword';
      case 'xls':
      case 'xlsx': return 'application/vnd.ms-excel';
      case 'ppt':
      case 'pptx': return 'application/vnd.ms-powerpoint';
      case 'zip': return 'application/zip';
      case 'msg':
      case 'eml': return 'application/vnd.ms-outlook';
      case 'mp2':
      case 'mpa':
      case 'mpe':
      case 'mpeg':
      case 'mpg':
      case 'mpv2': return 'audio/mpeg';
      default: return null;
    }
  }

  /**
   * Checks if the MIME type is previewable in the UI.
   */
  isVisualizable(tipo_archivo: string): boolean {
    const mimeType = this.getMimeType(tipo_archivo);
    return !!mimeType && this.visualizables.includes(mimeType);
  }

    /**
   * Initiates a secure download of a file from the backend.
   *
   * This method generates a temporary access token for the requested file,
   * validates that the token exists, and triggers a browser download.
   * The file is not loaded into Angular memory; the download is handled
   * by the browser via a hidden `<a>` element.
   *
   * The backend handles:
   * - Token validation
   * - Access control
   * - MIME detection
   * - File streaming for download
   *
   * @param id_archivo - Unique identifier of the file to download.
   * @param role - User role for access validation.
   */
  downloadFile(id_archivo: number, role: string) {
    // Show loading toast
    const toastRef = this.toastr.info(
      this.translate.instant('texto_723'),
      '',
      { disableTimeOut: true, closeButton: true }
    );

    // Generate token to construct file URL
    this.apiService.generateTokenToViewDocs(id_archivo, role).subscribe({
      next: (resp) => {

        if (!resp?.data.token) {
          this.toastr.error(this.translate.instant('texto_927'));
          return;
        }

        // Get document stream URL
        const fileUrl =
          `${environment.baseUrl}/api/Denuncias/complaint-documents/downloadDocument.php` +
          `?token=${encodeURIComponent(resp.data.token)}`;

        // Perform a HEAD request to:
        // - Validate token
        // - Confirm file exists
        this.http
          .head(fileUrl, { observe: 'response' })
          .subscribe({
            next: (res) => {
              const a = document.createElement('a');
              a.href = fileUrl;
              a.download = '';
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);

               // Close loading toast
              this.toastr.clear(toastRef.toastId);
              this.toastr.success(this.translate.instant('texto_724'));
            },
            error: (err) => {
              this.toastr.clear(toastRef.toastId);
              // Error handling
              this.toastr.error(
                this.translate.instant('texto_204'),
                this.translate.instant('texto_813')
              );
            }
          });

      },
      error: () => {
        this.toastr.clear(toastRef.toastId);
        this.toastr.error(
          this.translate.instant('texto_204'),
          this.translate.instant('texto_813')
        );
      }
    });
  }



  /**
   * Streams and displays a supported file inside an iframe viewer.
   *
   * This method DOES NOT download the file into Angular.
   * Instead, it generates a secure stream URL (token-based),
   * validates access using an HTTP HEAD request, and lets the iframe
   * consume the file directly via backend streaming (GET request).
   *
   * The backend handles:
   * - Token validation
   * - Access control
   * - MIME detection
   * - Chunked file streaming
   *
   * @param id_archivo - Unique identifier of the file.
   * @param tipo_archivo - File extension/type used to infer MIME.
   * @param role - User role for access validation.
   * @param callback - Receives the stream URL and final MIME type for rendering.
   */
  viewFile(
    id_archivo: number,
    tipo_archivo: string,
    n_archivo: string,
    role: string,
    callback: (url: string, mimeType: string) => void
  ) {
    // Determine MIME type and validate
    this.mimeType = this.getMimeType(tipo_archivo);
    if (!this.mimeType) {
      this.toastr.error(this.translate.instant('texto_181'));
      return;
    }

    // Check if the MIME type is visualizable
    if (!this.visualizables.includes(this.mimeType)) {
      this.toastr.error(this.translate.instant('texto_927'));
      return;
    }

    // Show loading toast
    const toastRef = this.toastr.info(
      this.translate.instant('texto_203'),
      '',
      { disableTimeOut: true, closeButton: true }
    );

    // Generate token to construct file URL
    this.apiService.generateTokenToViewDocs(id_archivo, role).subscribe({
      next: (resp) => {

        if (!resp?.data.token) {
          this.toastr.error(this.translate.instant('texto_927'));
          return;
        }

        // Set url that will call streamDocument.php through backend/.htaccess, the file name is concatenated to be displayed in the iframe title
        const fileUrl =
          `${environment.baseUrl}/api/Denuncias/complaint-documents/ver-documento/${encodeURIComponent(n_archivo)}` +
          `?token=${encodeURIComponent(resp.data.token)}`;

        // Perform a HEAD request to:
        // - Validate token
        // - Confirm file exists
        // - Avoid downloading the file
        // The actual file stream (GET) will be triggered by the iframe itself
        this.http
          .head(fileUrl, { observe: 'response' })
          .subscribe({
            next: (res) => {
              // Render file in iframe
              this.url = fileUrl;

              // MIME types that should NOT be forced to PDF
              const nonPdfMimes = [
                'audio/mpeg',
                'image/jpeg',
                'image/png',
                'image/gif',
                'video/mp4'
              ];

              // Default to PDF rendering when possible
              if (!nonPdfMimes.includes(this.mimeType)) {
                this.mimeType = 'application/pdf';
              }
              // Close loading toast
              this.toastr.clear(toastRef.toastId);
              callback(fileUrl, this.mimeType);
            },
            error: (err) => {
              this.toastr.clear(toastRef.toastId);
              // Error handling
              this.toastr.error(
                this.translate.instant('texto_204'),
                this.translate.instant('texto_813')
              );
            }
          });

      },
      error: () => {
        this.toastr.clear(toastRef.toastId);
        this.toastr.error(
          this.translate.instant('texto_204'),
          this.translate.instant('texto_813')
        );
      }
    });
  }

   /**
   * Downloads multiple documents as a ZIP file.
   * @param ids Array of document IDs to download.
   * @param id_denuncia The complaint ID for naming the ZIP file.
   */
  downloadDocumentsZip(ids: string[], id_denuncia: string) {
     // Show loading toast
    const toastRef = this.toastr.info(
      this.translate.instant('texto_723'),
      '',
      { disableTimeOut: true, closeButton: true }
    );

    // Call API to download documents as ZIP
    this.apiService.downloadDocumentsZip(ids, id_denuncia).subscribe(
      (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${id_denuncia}.zip`;
        a.click();
        window.URL.revokeObjectURL(url);

        this.toastr.clear(toastRef.toastId);
        this.toastr.success(this.translate.instant('texto_724'));
      },
      err => {
        this.toastr.clear(toastRef.toastId);
        this.toastr.error(
                this.translate.instant('texto_204'),
                this.translate.instant('texto_813')
              );
      }
    );
  }

}



